import { useEffect, useRef, useState, type JSX } from 'react';
import { mensagemDoErro } from '../lib/api.js';
import {
  fracaoParaPercentual,
  freteParaCampo,
  lerFreteRsKg,
  lerRtPercentual,
} from '../lib/numero.js';
import { useGerador, type Orcamento } from '../estado/gerador.js';
import './ParametrosOrcamento.css';

/**
 * RT (%) e frete (R$/kg) do ORÇAMENTO — Story C, endurecida.
 *
 * ## Por que estes dois campos ficam no TOPO
 *
 * Eles são da aba `Variáveis` da planilha: **um** RT e **um** frete governam o
 * orçamento inteiro. Antes viviam como células por linha, em R$/m², e o
 * resultado foi o ORC-2026-0007 — alguém digitou `5` querendo "5%" numa célula
 * que era reais, e o orçamento saiu R$ 66 mais barato sem nada acusar.
 *
 * ## Digitação livre, com rede
 *
 * Os campos sempre foram de digitação livre — não havia seletor. O que faltava
 * era a rede embaixo, e ela tinha dois furos medidos:
 *
 * - **`1.10` virava R$ 110,00/kg.** O parser removia todos os pontos antes de
 *   trocar a vírgula, para tratar milhar. Cem vezes o valor, em silêncio.
 * - **A tela ensinava o ponto.** O RT era formatado com `String(Number(...))` e
 *   aparecia `7.5`, ao lado de um frete `1,10`. O app escrevia com ponto num
 *   campo e punia o ponto no vizinho.
 *
 * Agora há um parser só (`lib/numero.ts`), três desfechos em vez de dois —
 * aceita, aceita-com-ressalva, recusa — e o campo se **reescreve normalizado**
 * ao sair, para que o vendedor veja o que o sistema entendeu.
 *
 * ## A conversão acontece na borda, e só nela
 *
 * O banco guarda **fração** (`0.07`); a tela mostra **percentual** (`7`). O
 * `÷100` mora em `lerRtPercentual` e em lugar nenhum além dele.
 *
 * ## Edição por confirmação, não por tecla
 *
 * Mesmo desenho da `LinhaItem`: rascunho local, salva no `blur` ou Enter, e só
 * se mudou. Aqui pesa mais — mudar o RT recalcula **todos** os itens, e salvar
 * a cada dígito faria o total dançar enquanto se digita "7,5".
 *
 * ## IPI — S8
 *
 * A terceira decisão do orçamento inteiro: aplicar ou não o IPI dos produtos.
 * É liga/desliga, então é um checkbox e não um campo — salva no clique, pelo
 * mesmo `definirParametros`, e a tela recarrega com os totais recalculados.
 */

/**
 * O texto do toggle, como a spec o nomeia.
 *
 * ⚠️ O `0,65%` é o IPI que o catálogo inteiro tem hoje (o `IPI_PADRAO` da
 * importação), escrito aqui como TEXTO. O cálculo não lê daqui: desligar zera o
 * IPI de cada produto, seja ele qual for. Se um dia houver produto com outro
 * IPI, este rótulo passa a mentir — e o certo será trazer o percentual da API.
 */
const ROTULO_IPI = 'Aplicar IPI (0,65%)';

/**
 * O nome do campo de RT — S12.
 *
 * Em Engenharia (CPF) a tabela vem SEM RT da Villagres, e o percentual daqui é
 * SOMADO ao preço: "RT a adicionar" diz isso. Nas outras o RT já vem embutido
 * na tabela, e o campo continua "RT". O ORC-2026-0053 zerou este campo em CPF
 * achando que reproduzia a tabela com RT.
 */
function tituloDoRt(tabela: string): string {
  return tabela === 'CPF' ? 'RT a adicionar (%)' : 'RT';
}

interface Props {
  orcamento: Orcamento;
  editavel: boolean;
}

type Campo = 'rt' | 'frete';
/** `erro` recusa e não grava; `atencao` gravou, mas o número merece um olhar. */
interface Nota {
  campo: Campo;
  texto: string;
  tom: 'erro' | 'atencao';
}

export function ParametrosOrcamento({ orcamento, editavel }: Props): JSX.Element {
  const definirParametros = useGerador((e) => e.definirParametros);

  /*
   * O que vale hoje: o override quando existe, senão o padrão do tenant. É o
   * mesmo `??` da precedência do servidor — a tela não inventa uma segunda
   * regra, espelha a que calcula.
   */
  const rtEfetivo = orcamento.rtPercentManual ?? orcamento.rtPercentPadrao ?? null;
  const freteEfetivo = orcamento.freteRsKgManual ?? orcamento.freteRsKgPadrao ?? null;

  const [rt, setRt] = useState(() => fracaoParaPercentual(rtEfetivo));
  const [frete, setFrete] = useState(() => freteParaCampo(freteEfetivo));
  const [nota, setNota] = useState<Nota | undefined>();

  /*
   * IPI (S8). Ausente lê-se como APLICADO: é o que uma API anterior à S8
   * faz, e o campo é opcional por causa do deploy escalonado.
   */
  const ipiAplicado = orcamento.aplicarIpi !== false;
  /**
   * O valor que a pessoa acabou de clicar, enquanto o PATCH está no ar.
   *
   * Não é atualização otimista dos TOTAIS — esses só mudam quando o servidor
   * devolve. É só o checkbox refletir o clique: um controle que não muda ao
   * ser clicado parece quebrado numa conexão de celular. Falhou, volta ao que
   * o servidor diz.
   */
  const [ipiPendente, setIpiPendente] = useState<boolean | undefined>();
  const [erroIpi, setErroIpi] = useState<string | undefined>();

  async function alternarIpi(aplicar: boolean): Promise<void> {
    setErroIpi(undefined);
    setIpiPendente(aplicar);
    const ok = await definirParametros({ aplicarIpi: aplicar });
    setIpiPendente(undefined);
    if (!ok) setErroIpi(mensagemDoErro(useGerador.getState().erro));
  }

  /*
   * Referências para saber QUEM está com o foco.
   *
   * Os campos deixaram de ficar `disabled` durante o salvamento: ajustar o RT,
   * dar Tab para o frete e começar a digitar fazia as teclas caírem no vazio
   * enquanto o PATCH estava no ar — reproduzível em qualquer conexão de
   * celular, e sem nada na tela explicando.
   *
   * Só que destravar sozinho troca um defeito por outro: quando a resposta
   * chega, o efeito abaixo reescreveria o campo e apagaria o que a pessoa
   * acabou de digitar. Daí a regra: **o servidor não pisa em quem está
   * digitando**.
   */
  const refRt = useRef<HTMLInputElement>(null);
  const refFrete = useRef<HTMLInputElement>(null);

  /*
   * Ressincroniza quando o servidor devolve outro valor — inclusive depois de
   * um `null` que devolveu o campo ao padrão do tenant. Sem isto, limpar o
   * override deixaria o campo mostrando o valor antigo.
   */
  useEffect(() => {
    if (document.activeElement === refRt.current) return;
    setRt(fracaoParaPercentual(rtEfetivo));
  }, [rtEfetivo]);
  useEffect(() => {
    if (document.activeElement === refFrete.current) return;
    setFrete(freteParaCampo(freteEfetivo));
  }, [freteEfetivo]);

  /** Ajustado = há override E ele difere do padrão do tenant. */
  const rtAjustado =
    orcamento.rtPercentManual != null &&
    Number(orcamento.rtPercentManual) !== Number(orcamento.rtPercentPadrao ?? NaN);
  const freteAjustado =
    orcamento.freteRsKgManual != null &&
    Number(orcamento.freteRsKgManual) !== Number(orcamento.freteRsKgPadrao ?? NaN);

  /** Como o campo deve estar escrito para o valor que hoje vale. */
  function comoSeEscreve(campo: Campo): string {
    return campo === 'rt' ? fracaoParaPercentual(rtEfetivo) : freteParaCampo(freteEfetivo);
  }

  function escrever(campo: Campo, texto: string): void {
    if (campo === 'rt') setRt(texto);
    else setFrete(texto);
  }

  async function confirmar(campo: Campo, texto: string): Promise<void> {
    setNota(undefined);
    const chave = campo === 'rt' ? 'rtPercentManual' : 'freteRsKgManual';

    /*
     * Campo esvaziado ⇒ volta ao padrão do tenant. É como se desfaz um ajuste
     * sem precisar de um botão "limpar" ao lado de cada campo.
     *
     * E o campo **reexibe o padrão**: deixá-lo em branco mostrando só o
     * placeholder faria o mesmo orçamento se apresentar de dois jeitos
     * conforme o caminho (recém-carregado vs recém-esvaziado).
     */
    if (texto.trim() === '') {
      if (orcamento[chave] == null) {
        escrever(campo, comoSeEscreve(campo)); // já estava no padrão
        return;
      }
      const ok = await definirParametros({ [chave]: null });
      if (!ok) {
        escrever(campo, comoSeEscreve(campo));
        setNota({ campo, texto: mensagemDoErro(useGerador.getState().erro), tom: 'erro' });
      }
      return;
    }

    const lido =
      campo === 'rt'
        ? lerRtPercentual(texto, fracaoParaPercentual(orcamento.rtPercentPadrao))
        : lerFreteRsKg(texto);
    if (!lido.ok) {
      /*
       * Recusa de FORMATO mantém o texto na tela — quem errou a digitação
       * precisa ver o que digitou para corrigir. É o oposto da falha de rede
       * lá embaixo, onde o texto vira mentira.
       */
      setNota({ campo, texto: lido.mensagem, tom: 'erro' });
      return;
    }

    const ressalva = lido.alerta;

    /*
     * Nada a enviar quando o valor não mudou — mas o campo **é reescrito**
     * mesmo assim. Digitar `1.10` num frete que já vale 1,10 não gera PATCH, e
     * sem esta linha o campo continuaria exibindo `1.10` para sempre: a tela
     * ficaria ensinando exatamente a notação que a correção veio consertar.
     *
     * A comparação é NUMÉRICA, não de string: o servidor devolve `1.100000` e
     * a conversão produz `1.100000`, mas `1,1` digitado também — comparar
     * texto acusaria mudança onde não há.
     */
    const vigente =
      orcamento[chave] ?? (campo === 'rt' ? orcamento.rtPercentPadrao : orcamento.freteRsKgPadrao);
    if (vigente != null && Number(lido.valor) === Number(vigente)) {
      escrever(campo, comoSeEscreve(campo));
      if (ressalva !== undefined) setNota({ campo, texto: ressalva, tom: 'atencao' });
      return;
    }

    const ok = await definirParametros({ [chave]: lido.valor });
    if (!ok) {
      /*
       * O PATCH falhou: o servidor tem o valor ANTIGO e o campo mostra o novo.
       * Sem reverter, a tela mente até a próxima montagem — e o vendedor pode
       * emitir (imutável!) acreditando num frete que nunca foi gravado.
       */
      escrever(campo, comoSeEscreve(campo));
      setNota({ campo, texto: mensagemDoErro(useGerador.getState().erro), tom: 'erro' });
      return;
    }
    // Gravou. O `useEffect` reescreve o campo a partir do que o servidor
    // devolveu; a ressalva, se houver, fica visível ao lado.
    if (ressalva !== undefined) setNota({ campo, texto: ressalva, tom: 'atencao' });
  }

  if (!editavel) {
    // Emitido: o valor congelado, sem campo. Editar aqui devolveria 409, e um
    // campo que só sabe recusar é pior que nenhum.
    return (
      <div className="parametros parametros--leitura">
        <span className="parametros__item">
          <span className="parametros__rotulo">{tituloDoRt(orcamento.tabela)}</span>
          <span className="parametros__valor num">{fracaoParaPercentual(rtEfetivo) || '—'}%</span>
        </span>
        <span className="parametros__item">
          <span className="parametros__rotulo">Frete</span>
          <span className="parametros__valor num">
            {freteParaCampo(freteEfetivo) || '—'} <small>R$/kg</small>
          </span>
        </span>
        <span className="parametros__item">
          <span className="parametros__rotulo">IPI</span>
          <span className="parametros__valor">{ipiAplicado ? 'aplicado' : 'não aplicado'}</span>
        </span>
      </div>
    );
  }

  /** Os dois campos são o mesmo desenho — a diferença cabe nestes dados. */
  const campos = [
    {
      id: 'rt' as const,
      titulo: tituloDoRt(orcamento.tabela),
      unidade: '%',
      valor: rt,
      ajustado: rtAjustado,
      /*
       * O nome acessível CONTÉM o rótulo visível (WCAG 2.5.3): em Engenharia o
       * leitor de tela precisa ouvir "a adicionar", senão perde o que a S12 diz.
       */
      rotuloAria:
        orcamento.tabela === 'CPF'
          ? 'RT a adicionar (%) do orçamento'
          : 'RT do orçamento em porcento',
      ref: refRt,
      padrao: fracaoParaPercentual(orcamento.rtPercentPadrao),
    },
    {
      id: 'frete' as const,
      titulo: 'Frete',
      unidade: 'R$/kg',
      valor: frete,
      ajustado: freteAjustado,
      rotuloAria: 'Frete do orçamento em reais por quilo',
      ref: refFrete,
      padrao: freteParaCampo(orcamento.freteRsKgPadrao),
    },
  ];

  return (
    <div className="parametros">
      {campos.map((c) => (
        <label className="parametros__campo" key={c.id}>
          <span className="parametros__rotulo">
            {c.titulo}
            {c.ajustado && (
              <span className="etiqueta etiqueta--aviso parametros__marca">ajustado</span>
            )}
          </span>
          <span className="parametros__entrada">
            <input
              className="entrada-num entrada-num--curta"
              inputMode="decimal"
              value={c.valor}
              ref={c.ref}
              aria-label={c.rotuloAria}
              aria-invalid={nota?.campo === c.id && nota.tom === 'erro'}
              placeholder={c.padrao}
              onChange={(e) => {
                escrever(c.id, e.target.value);
                setNota(undefined);
              }}
              onBlur={() => {
                void confirmar(c.id, c.valor);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
            />
            {/* A unidade ao lado do campo — a ausência dela é o defeito que a
                Story C veio corrigir. */}
            <span className="parametros__unidade">{c.unidade}</span>
          </span>
          {nota?.campo === c.id && (
            <span
              className={`parametros__aviso parametros__aviso--${nota.tom}`}
              // `alert` só para o que RECUSOU. A ressalva já gravou: interromper
              // o leitor de tela por um valor aceito é ruído.
              role={nota.tom === 'erro' ? 'alert' : 'status'}
            >
              {nota.texto}
            </span>
          )}
        </label>
      ))}

      <div className="parametros__campo">
        <span className="parametros__rotulo">
          IPI
          {!ipiAplicado && (
            <span className="etiqueta etiqueta--aviso parametros__marca">não aplicado</span>
          )}
        </span>
        {/* O `label` envolve só o checkbox e o texto dele — é o nome acessível. */}
        <label className="parametros__entrada parametros__alternar">
          <input
            type="checkbox"
            checked={ipiPendente ?? ipiAplicado}
            // Travado só durante o PATCH: dois cliques rápidos mandariam dois.
            disabled={ipiPendente !== undefined}
            onChange={(e) => {
              void alternarIpi(e.target.checked);
            }}
          />
          {ROTULO_IPI}
        </label>
        {erroIpi !== undefined && (
          <span className="parametros__aviso parametros__aviso--erro" role="alert">
            {erroIpi}
          </span>
        )}
      </div>

      <p className="parametros__dica">
        Valem para <strong>todos os itens</strong> deste orçamento. Aceita vírgula ou ponto — deixe
        em branco para usar o padrão da empresa.
      </p>
    </div>
  );
}
